// Parses free text typed or pasted into the dashboard Quick Order box.
//
// Accepts one line per part. Each line is "<part number> <quantity>", where the
// separator can be a tab (Excel paste), comma, semicolon, or whitespace before a
// trailing number. Part numbers may contain spaces ("SP 1/4 BSP"), so without an
// explicit tab/comma/semicolon only a trailing whole number is read as quantity.
//
// Returns [{ partNumber, quantity }] with duplicate part numbers merged.
// quantity is null when none was given, so callers can ask rather than guess.
export function parseQuickOrder(text) {
  const merged = new Map();

  (text || '').split(/\r?\n/).forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (!line) return;

    let partNumber;
    let qtyText = null;

    if (/[\t,;]/.test(line)) {
      const [first, second] = line.split(/[\t,;]/).map((f) => f.trim());
      partNumber = first;
      qtyText = second || null;
    } else {
      const match = line.match(/^(.+?)\s+(\d+)$/);
      partNumber = match ? match[1].trim() : line;
      qtyText = match ? match[2] : null;
    }

    const quantity = qtyText !== null && /^\d+$/.test(qtyText) && Number(qtyText) > 0
      ? Number(qtyText)
      : null;

    // A spreadsheet header row ("PartNumber, Quantity") has a non-numeric qty column.
    if (index === 0 && qtyText !== null && quantity === null && /part/i.test(partNumber)) return;
    if (!partNumber) return;

    const key = partNumber.toUpperCase();
    const existing = merged.get(key);
    if (existing) {
      if (quantity !== null) existing.quantity = (existing.quantity || 0) + quantity;
    } else {
      merged.set(key, { partNumber, quantity });
    }
  });

  return Array.from(merged.values());
}

// Decides what the Quick Order box should do with its text.
// A single line without an explicit tab/comma/semicolon is always a part search,
// because "SP 212" is more likely a part number than part "SP" x 212.
// Returns { type: 'search', term } | { type: 'order', lines } | { type: 'empty' }.
export function interpretQuickOrder(text) {
  const nonEmpty = (text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (nonEmpty.length === 0) return { type: 'empty' };
  if (nonEmpty.length === 1 && !/[\t,;]/.test(nonEmpty[0])) {
    return { type: 'search', term: nonEmpty[0] };
  }
  const lines = parseQuickOrder(text);
  return lines.length ? { type: 'order', lines } : { type: 'empty' };
}
